import { Button, Space } from 'antd';
import React, { useState } from 'react';
import { useAtomValue } from 'jotai';
import { Header } from './styled';
import { NOC_DRILLDOWN, isAppModule, ADMIN_ROLE_NAME } from '@src/utils/constants';
import {
  appExperienceShareAndSaveSelector,
  getUserProfile,
  intervalSelector,
  publicSavedDashboardsSelector,
} from '@src/store/common/selectors';
import { useSelector } from 'react-redux';
import { prefixedCheckFeature } from '@src/utils/platform/features/checkFeature';
import { modeSelector } from '@src/store/trends/selectors';
import SavingWrapper from '@components/saving/SavingWrapper/SavingWrapper';
import { showRollingWindow } from '@src/utils/dashboard/rollingWindow';
import { DeleteOutlined, SaveOutlined, CopyOutlined } from '@ant-design/icons';
import DeleteSavedDashboardWrapper from '@components/saving/DeleteSavedDashboardWrapper/DeleteSavedDashboardWrapper';
import PresetPersistence from './PresetPersistence';
import { MODE_AI_ALERT_DIAGNOSTICS } from '@src/store/trends/constants';
import DefaultPageOption from './DefaultPageOption';
import DefaultLandingPrompt from '@components/common/DefaultLandingPage';
import { useDefaultLandingPageStore } from '@globalStore/defaultLandingPageStore';
import { savedDashboardCreatorIdAtom } from '@src/components/saving/atoms';
import { pageMetaAtom } from '@src/atoms/pageMeta';
import useDefaultLandingPage from '../hooks/useDefaultLandingPage';
import { track } from '@src/utils';
import {
  CLICK_ON_DELETE_DASHBOARD,
  CLICK_ON_UPDATE_DASHBOARD,
  CLICK_ON_CLONE_DASHBOARD,
  CLICK_SAVE_FROM_KEBAB,
} from '@src/utils/sensor/track';
import { ACTIONTYPES, ClickType } from '@src/components/saving/utils';

const cf = prefixedCheckFeature('trends.actions');

function Actions() {
  const { id, pageKey, isSavedDashboard, isSharing } = useAtomValue(pageMetaAtom);
  const savedDashboardCreatorId = useAtomValue(savedDashboardCreatorIdAtom);
  const { mode } = useSelector(modeSelector);
  const { profile_id: profileId, role } = useSelector(getUserProfile);

  const interval = useSelector(intervalSelector);
  const defaultLandingPage = useDefaultLandingPageStore((state) => ({
    url: state.url,
    name: state.name,
    path: state.path,
  }));

  const publicSavedDashboardsFeature = useSelector(publicSavedDashboardsSelector);
  const appExperienceShareAndSaveFeature = useSelector(appExperienceShareAndSaveSelector);

  // FF check, default to false
  const hasSaveFeature =
    publicSavedDashboardsFeature || appExperienceShareAndSaveFeature || cf('save', false);

  // show save option if not a saved dashboard
  // or show update option if it's a saved dashboard, and creatorId matches current profileId / current user has admin role
  const userIsCreator = savedDashboardCreatorId === profileId;
  const userIsAdmin = role === ADMIN_ROLE_NAME;
  const shouldShowSaveOption =
    hasSaveFeature && (!isSavedDashboard || userIsCreator || userIsAdmin);
  const shouldShowDeleteOption = hasSaveFeature && isSavedDashboard && userIsCreator;
  const [clickType, setClickType] = useState<ClickType>(ACTIONTYPES.DEFAULT);

  //TODO: default landing page code needs a re-write
  const hideMakeDefaultOption =
    isSharing || [MODE_AI_ALERT_DIAGNOSTICS, NOC_DRILLDOWN].includes(mode);
  const defaultPathname = defaultLandingPage.url || '';
  const { pathname } = window.location;
  const isDefaultLandingPage = defaultPathname.endsWith(pathname);

  const { onSetDefaultLanding } = useDefaultLandingPage();

  const onSaveClick = () => {
    setClickType(ACTIONTYPES.SAVE);
    track({ event: CLICK_SAVE_FROM_KEBAB });
  };
  const onUpdateClick = () => {
    setClickType(ACTIONTYPES.UPDATE);
    track({ event: CLICK_ON_UPDATE_DASHBOARD });
  };
  const onDeleteClick = () => {
    track({ event: CLICK_ON_DELETE_DASHBOARD });
  };

  const onCloneClick = () => {
    setClickType(ACTIONTYPES.CLONE);
    track({ event: CLICK_ON_CLONE_DASHBOARD });
  };

  return (
    <Space direction="vertical">
      <Header>Actions</Header>

      {shouldShowSaveOption && (
        <SavingWrapper
          pageMeta={{
            page: { key: pageKey },
            showRollingWindowOption: showRollingWindow(interval),
          }}
          type={clickType}
        >
          {isSavedDashboard ? (
            <>
              <Button onClick={onUpdateClick} type="text" size="small" icon={<SaveOutlined />}>
                Update
              </Button>
              <Button onClick={onCloneClick} type="text" size="small" icon={<CopyOutlined />}>
                Clone
              </Button>
            </>
          ) : (
            <Button onClick={onSaveClick} type="text" size="small" icon={<SaveOutlined />}>
              Save
            </Button>
          )}
        </SavingWrapper>
      )}

      {shouldShowDeleteOption && (
        <DeleteSavedDashboardWrapper
          pageMeta={{
            id,
          }}
          dashboardTypeName={isAppModule ? 'App_Experience' : 'TRENDS'}
        >
          <Button onClick={onDeleteClick} type="text" size="small" icon={<DeleteOutlined />}>
            Delete
          </Button>
        </DeleteSavedDashboardWrapper>
      )}

      {!hideMakeDefaultOption && cf('make-default', true) && (
        <DefaultPageOption
          context={null}
          clickCallback={onSetDefaultLanding}
          disabled={isDefaultLandingPage}
          showIcon={true}
        />
      )}
      <PresetPersistence />
      <DefaultLandingPrompt />
    </Space>
  );
}

export default Actions;
